import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Header, Footer } from "../components/Orchestrio";

const WorkPage = () => {
  const caseStudies = [
    {
      client: "Legal Partners LLP",
      industry: "Legal Services",
      type: "Client Portal",
      challenge: "Support team overwhelmed by routine document requests. Clients frustrated by slow response times.",
      solution: "Self-service client portal with secure document access, matter tracking, and automated notifications.",
      outcome: "70% reduction in support queries. Client satisfaction scores up 40%.",
      tech: ["React", "Node.js", "PostgreSQL", "AWS"],
      timeline: "12 weeks"
    },
    {
      client: "Northern Supplies",
      industry: "Distribution",
      type: "Order Processing System",
      challenge: "Manual order processing taking 4+ hours per day. Frequent errors causing delivery delays.",
      solution: "Automated order pipeline with supplier integrations, stock management, and exception handling.",
      outcome: "Processing time reduced from 4 hours to 10 minutes. Error rate down 95%.",
      tech: ["Python", "FastAPI", "PostgreSQL", "Celery"],
      timeline: "8 weeks"
    },
    {
      client: "Bloom Health",
      industry: "Healthcare",
      type: "Booking Platform",
      challenge: "Outdated booking system causing drop-offs. No visibility into capacity utilisation.",
      solution: "Modern booking flow with real-time availability, automated reminders, and admin dashboard.",
      outcome: "3x booking conversion rate. 25% improvement in practitioner utilisation.",
      tech: ["Next.js", "TypeScript", "MongoDB", "Stripe"],
      timeline: "10 weeks"
    },
    {
      client: "Crawford & Sons Logistics",
      industry: "Logistics",
      type: "Fleet Management Dashboard",
      challenge: "No real-time visibility into vehicle locations. Manual reporting taking hours each week.",
      solution: "Real-time tracking dashboard with automated reporting, driver communication, and route optimisation.",
      outcome: "£45k annual savings in fuel costs. Management reporting reduced from 5 hours to 15 minutes.",
      tech: ["React", "Node.js", "MongoDB", "Google Maps API"],
      timeline: "14 weeks"
    },
    {
      client: "GreenTech Solutions",
      industry: "Renewable Energy",
      type: "Marketing Website & CRM Integration",
      challenge: "Outdated website hurting credibility. Leads getting lost between systems.",
      solution: "Modern marketing site with integrated lead capture, CRM sync, and automated nurture sequences.",
      outcome: "Organic traffic tripled. Lead response time reduced from 24 hours to 5 minutes.",
      tech: ["Next.js", "Tailwind CSS", "HubSpot API", "Vercel"],
      timeline: "6 weeks"
    },
    {
      client: "PropertyMark",
      industry: "Property",
      type: "Property Management System",
      challenge: "Multiple spreadsheets tracking different properties. No unified view of portfolio performance.",
      solution: "Custom property management system with tenant portal, maintenance tracking, and financial reporting.",
      outcome: "£30k saved in admin time annually. Zero missed rent reviews.",
      tech: ["React", "Python", "PostgreSQL", "AWS"],
      timeline: "16 weeks"
    }
  ];

  return (
    <div className="min-h-screen bg-[#1E1E1E]" data-testid="work-page">
      <Header />

      {/* Header */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-[#5F8FB8] text-sm font-medium tracking-wide mb-6">
            Work
          </p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            Selected projects
          </h1>
          <p className="text-lg text-[#9CA3AF] max-w-2xl leading-relaxed">
            Systems we've built. Problems we've solved. 
            Each project includes the challenge, our solution, and the measurable outcome.
          </p>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="space-y-12">
            {caseStudies.map((study, index) => (
              <div 
                key={index}
                className="p-8 bg-[#242424] border border-[#2E2E2E]"
              >
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-[#5F8FB8] text-sm font-medium">{study.type}</span>
                      <span className="text-[#3A3A3A]">·</span>
                      <span className="text-[#6B7280] text-sm">{study.industry}</span>
                    </div>
                    <h2 className="text-xl font-bold">{study.client}</h2>
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-[#6B7280]">Timeline</span>
                    <p className="font-semibold">{study.timeline}</p>
                  </div>
                </div>

                {/* Content */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div>
                    <h3 className="text-sm font-semibold text-[#6B7280] uppercase tracking-wide mb-2">
                      Challenge
                    </h3>
                    <p className="text-sm text-[#9CA3AF] leading-relaxed">{study.challenge}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-[#6B7280] uppercase tracking-wide mb-2">
                      Solution
                    </h3>
                    <p className="text-sm text-[#9CA3AF] leading-relaxed">{study.solution}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-[#6B7280] uppercase tracking-wide mb-2">
                      Outcome
                    </h3>
                    <p className="text-sm text-[#F4F6F8] leading-relaxed font-medium">{study.outcome}</p>
                  </div>
                </div>

                {/* Tech stack */}
                <div className="mt-6 pt-6 border-t border-[#2E2E2E]">
                  <span className="text-sm text-[#6B7280]">Technologies: </span>
                  <span className="text-sm text-[#9CA3AF]">{study.tech.join(", ")}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
            <div>
              <h2 className="text-2xl font-bold mb-3">Have a similar challenge?</h2>
              <p className="text-[#9CA3AF] max-w-lg">
                Let's discuss your project. We'll tell you honestly whether we're the right fit 
                and how we'd approach it.
              </p>
            </div>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2 flex-shrink-0">
              Get in touch <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WorkPage;
